import { useQuery } from "@tanstack/react-query";

import { listContent, listUsers } from "@/lib/admin-api";

const TOTAL_USERS_COUNT_QUERY_KEY = ["home", "total-users-count"];
const TOTAL_CONTENT_COUNT_QUERY_KEY = ["home", "total-content-count"];

/**
 * Total user count for the Home launcher's Users & Access card. Cheap by
 * construction, same trick as use-moderation-pending-count.ts: listUsers
 * already returns pageInfo.totalCount, so a page_size: 1 request is enough -
 * no need to pull the list itself.
 *
 * Returns `undefined` (render the card without a number) rather than a
 * fabricated total if the backend omits totalCount, or if the request fails
 * outright. Home is a launcher; a failed count degrades the one card to its
 * plain label, it does not block the page.
 */
export function useTotalUsersCount(): number | undefined {
  const { data } = useQuery({
    queryKey: TOTAL_USERS_COUNT_QUERY_KEY,
    queryFn: async () => {
      const response = await listUsers({ pageSize: 1, page: 1 });
      // react-query treats `undefined` from a queryFn as a usage error, so
      // `null` is the sentinel that comes back out of the cache - translated
      // to `undefined` below, the "render nothing" contract this hook
      // promises its caller.
      return response.pageInfo?.totalCount ?? null;
    },
  });

  return data ?? undefined;
}

/**
 * Total content count for the Home launcher's Content card. Same
 * page_size: 1 / totalCount pattern as useTotalUsersCount above.
 */
export function useTotalContentCount(): number | undefined {
  const { data } = useQuery({
    queryKey: TOTAL_CONTENT_COUNT_QUERY_KEY,
    queryFn: async () => {
      const response = await listContent({ pageSize: 1, page: 1 });
      return response.pageInfo?.totalCount ?? null;
    },
  });

  return data ?? undefined;
}
